/*
    The Monitor page's Date Filter (MPN-1530 §1.2).

    One place that turns a preset into the inclusive [start, end] the analytics
    endpoint takes. The server deliberately knows nothing about "Last 6 months":
    it takes two dates, and what a preset means is a presentation decision that
    belongs here, next to the dropdown that offers it.

    Ranges end TODAY. Today is only dropped where a part-day actually lies, which
    is narrower than it first looked (MPN-1548, then MPN-1567).

    MPN-1548 excluded today from the whole page. That was too broad. The distortion
    it was aimed at is a RATE one: at 7am Rachel's campaign had 7 impressions and
    $0.50 of spend, which as a CPM read $71 against a normal $20-40 -- the
    denominator is still filling, so the smaller it is the wilder the number. A
    COUNT has no such problem. "2 conversions so far today" is incomplete but not
    misleading, and hiding it cost a morning: Rachel loaded a file with two
    conversions, saw them on the Population page, and found zero on the Monitor
    page and the client dashboard (MPN-1567).

    So the partial day is dropped from the rate data instead, in one place -- see
    `performance-by-day` in ChartSerializer, which every rate on this page reads
    and nothing else does. Conversions, engagements and the volume bars keep
    today, where a short bar is honest rather than wrong.

    Deliberately NOT excluded anywhere: the Spend tab's Full Campaign panel, which
    CmdGetSpendPanels computes over the whole flight and documents as ignoring the
    Date Filter. Budget remaining has to count money spent today.

    SINCE_START resolves against the campaign's own start date, so it needs one
    passed in; with none available it falls back to all time, which is the
    honest answer when we do not know when the campaign began.
*/

export type DateFilterKey =
  | "last30"
  | "last60"
  | "last90"
  | "last120"
  | "last6months"
  | "lastYear"
  | "sinceStart"
  | "custom"

export type DateRange = { start: Date | null; end: Date | null }

export const DATE_FILTER_OPTIONS: { key: DateFilterKey; label: string }[] = [
  { key: "last30", label: "Last 30 days" },
  { key: "last60", label: "Last 60 days" },
  { key: "last90", label: "Last 90 days" },
  { key: "last120", label: "Last 120 days" },
  { key: "last6months", label: "Last 6 months" },
  { key: "lastYear", label: "Last Year" },
  { key: "sinceStart", label: "Since Campaign Start Date" },
  { key: "custom", label: "Custom" },
]

/*
    Since Campaign Start, not Last 30 days (MPN-1639).

    The narrower default hid real data and read as missing data. Lehigh Carbon's
    conversions were older than 30 days, so the Monitor page showed fewer than the
    Population page and the gap was chased as a bug until someone changed the filter.

    A default that answers "where's my data?" with all of it is the safer opening view:
    too much is a filter away from being narrowed, too little looks like a fault. The
    other seven presets are unchanged.
*/
export const DEFAULT_DATE_FILTER: DateFilterKey = "sinceStart"

const DAYS_BACK: Partial<Record<DateFilterKey, number>> = {
  last30: 30,
  last60: 60,
  last90: 90,
  last120: 120,
}

/** Midnight local, so a range never depends on the hour it was picked. */
function atMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function minusDays(from: Date, days: number): Date {
  const d = atMidnight(from)
  d.setDate(d.getDate() - days)
  return d
}

function minusMonths(from: Date, months: number): Date {
  const d = atMidnight(from)
  // setMonth clamps rather than rolling over: 31 Aug minus 6 months is 28 Feb,
  // not 2 Mar. Clamping is what a reader means by "6 months ago".
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() - months)
  d.setDate(Math.min(day, daysInMonth(d.getFullYear(), d.getMonth())))
  return d
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

/**
 * Resolve a preset to the inclusive range the analytics endpoint takes.
 *
 * `custom` returns nulls: the caller owns the picked dates and passes them
 * straight through, so there is nothing to compute here.
 */
export function resolveDateFilter(
  key: DateFilterKey,
  opts: { today?: Date; campaignStart?: Date | null } = {},
): DateRange {
  const today = atMidnight(opts.today ?? new Date())
  const end = today

  if (key === "custom") return { start: null, end: null }

  if (key === "sinceStart") {
    // No known start means no lower bound rather than a guessed one. The server
    // reads a null start as all-time, which is exactly right.
    return { start: opts.campaignStart ? atMidnight(opts.campaignStart) : null, end }
  }

  if (key === "last6months") return { start: minusMonths(today, 6), end }
  if (key === "lastYear") return { start: minusMonths(today, 12), end }

  const days = DAYS_BACK[key]
  return { start: days ? minusDays(today, days) : null, end }
}

/** ISO yyyy-mm-dd in LOCAL time. toISOString() would shift the date west of UTC. */
export function toApiDate(d: Date | null | undefined): string | undefined {
  if (!d) return undefined
  const month = `${d.getMonth() + 1}`.padStart(2, "0")
  const day = `${d.getDate()}`.padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

/**
 * Parse the server's yyyy-mm-dd as a LOCAL date.
 *
 * new Date("2026-07-14") is specified to parse a date-only string as UTC, so west
 * of Greenwich it becomes 13 Jul at 17:00 local and every display of it is a day
 * early. Splitting the parts and using the numeric constructor keeps the day the
 * server actually sent.
 */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const [year, month, day] = value.split("-").map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

/** Human form for the campaign header, e.g. "14 Jul 2026". */
export function formatFlightDate(value: string | null | undefined): string {
  const date = parseApiDate(value)
  if (!date) return "Not scheduled"
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

/*
    The campaign's start date (MPN-1578), formatted like the flight dates beside it.

    Its own function rather than a flag on formatFlightDate because the empty case means
    something different. A missing flight date is "Not scheduled" -- nobody has put this on
    the calendar. A missing start date is "Not started" -- it is on the calendar and has not
    published yet, which is a normal state for a campaign in onboarding, not a gap.
*/
export function formatStartDate(value: string | null | undefined): string {
  const date = parseApiDate(value)
  if (!date) return "Not started"
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

export function labelFor(key: DateFilterKey): string {
  return DATE_FILTER_OPTIONS.find((o) => o.key === key)?.label ?? key
}
